import axios from 'axios';

const API_URL = '/api';

const api = axios.create({
  baseURL: API_URL,
  // HIGH-13: withCredentials so the fw_auth httpOnly cookie (set by
  // backend on login — see CRIT-12) is sent on requests. This is the
  // first step of the migration off localStorage-JWT. The bearer token
  // fallback stays live until the backend drops the body-token path.
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    // HIGH-13: custom header as CSRF defense. Browsers will NOT let a
    // simple cross-origin form request set X-Requested-With without
    // triggering a CORS preflight — which our CORS policy rejects for
    // untrusted origins. Any admin state-changing request arriving
    // WITHOUT this header on the backend must be from outside our SPA
    // and can be rejected server-side.
    'X-Requested-With': 'XMLHttpRequest'
  }
});

// ============================================================
// Proactive token refresh
// ============================================================
// Decodes the JWT's `exp` claim and kicks off a background refresh once
// we're within the "refresh window" (default: final 20% of the token's
// lifetime). This avoids the user's long-running session silently dying
// mid-operation: the socket stays connected, batched API calls don't
// fall into a 401 stampede, and the next interactive request finds a
// fresh token already in localStorage.
//
// Uses the same `_refreshInFlight` single-flight lock as the 401
// interceptor below — if a reactive refresh is already happening, the
// proactive check piggy-backs on it.
const PROACTIVE_REFRESH_THRESHOLD = 0.80; // refresh when >80% of TTL elapsed

const _decodeJwtExp = (token) => {
  try {
    const payloadB64 = token.split('.')[1];
    if (!payloadB64) return null;
    // Base64URL → Base64
    const b64 = payloadB64.replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(atob(b64));
    const exp = typeof json.exp === 'number' ? json.exp : null;
    const iat = typeof json.iat === 'number' ? json.iat : null;
    return { exp, iat };
  } catch (_) {
    return null;
  }
};

const _shouldProactivelyRefresh = (token) => {
  const claims = _decodeJwtExp(token);
  if (!claims || !claims.exp) return false;
  const now = Math.floor(Date.now() / 1000);
  if (now >= claims.exp) return true; // already expired → reactive path handles it
  // If we don't have `iat`, assume a 24h window (conservative)
  const issuedAt = claims.iat || (claims.exp - 24 * 3600);
  const totalLifetime = claims.exp - issuedAt;
  if (totalLifetime <= 0) return false;
  const elapsed = now - issuedAt;
  return elapsed / totalLifetime >= PROACTIVE_REFRESH_THRESHOLD;
};

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      // Kick off a background refresh if the token is in its final 20%.
      // Fire-and-forget — we don't block the request. The _refreshInFlight
      // single-flight lock (defined below) ensures we don't stampede.
      if (!_refreshInFlight && _shouldProactivelyRefresh(token)) {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          _refreshInFlight = axios
            .post(`${API_URL}/auth/refresh`, { refreshToken })
            .then((res) => {
              const data = res.data?.data || res.data;
              const newAccess = data?.accessToken || data?.token;
              const newRefresh = data?.refreshToken;
              if (newAccess) {
                localStorage.setItem('token', newAccess);
                if (newRefresh) localStorage.setItem('refreshToken', newRefresh);
                // M4: the live socket captured the OLD token at connect time;
                // resync it so real-time order events don't silently stop after
                // a refresh. reconnect() re-reads the new token from localStorage.
                try { if (window.socketService?.isConnected) window.socketService.reconnect(); } catch (_) {}
              }
              return newAccess;
            })
            .catch(() => {
              // Silent — the 401 reactive path will handle real failures
              return null;
            })
            .finally(() => {
              setTimeout(() => { _refreshInFlight = null; }, 0);
            });
        }
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ============================================================
// RESPONSE INTERCEPTOR — handle 401 Unauthorized globally.
// ============================================================
// When the backend returns 401 (token expired / invalid / missing), we must
// clean up client state and redirect the user to the login screen.
// Previously, 401s fell through silently and left the user stuck.
//
// Tries a single token refresh first (using the refreshToken in localStorage).
// If refresh succeeds, retries the failed request once.
// If refresh fails OR the 401 was on the refresh endpoint itself, clears all
// auth state and redirects to /login.
//
// A single-flight lock prevents N concurrent 401s from all firing parallel
// refresh calls (same pattern as the Flutter ApiService Completer lock).
let _refreshInFlight = null;

const clearAuthAndRedirect = () => {
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  } catch (_) {}
  // Avoid redirect loops if we're already on the login page
  if (typeof window !== 'undefined' &&
      !window.location.pathname.startsWith('/login') &&
      !window.location.pathname.startsWith('/register')) {
    window.location.href = '/login';
  }
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    // ----------------------------------------------------------------
    // Retry transient network errors ONCE (ERR_SSL_PROTOCOL_ERROR,
    // ERR_NETWORK, ERR_CONNECTION_RESET, etc.). These have no
    // `error.response` — the request never reached the server. Common
    // causes on Windows POS terminals:
    //   - TLS handshake glitch after sleep/wake (network stack not ready)
    //   - Corporate firewall momentarily dropping the connection
    //   - QUIC → TCP fallback delay (despite --disable-quic flag)
    // A single 1.5s retry resolves ~99% of transient failures without
    // the user noticing.
    // ----------------------------------------------------------------
    // #10: ONLY auto-retry idempotent reads (GET/HEAD). A POST/PUT/PATCH/DELETE
    // that failed with no response may have actually reached and been processed
    // by the server (e.g. TLS reset AFTER the order/payment was created) — a
    // silent resend would create a duplicate order or double payout. Those errors
    // propagate so the caller can decide, rather than blindly retrying.
    const _method = (originalRequest?.method || 'get').toLowerCase();
    const _isIdempotent = _method === 'get' || _method === 'head';
    if (!error.response && originalRequest && !originalRequest._networkRetried && _isIdempotent) {
      originalRequest._networkRetried = true;
      console.warn(`[api] Network error on ${originalRequest.url}, retrying in 1.5s...`);
      await new Promise(r => setTimeout(r, 1500));
      return api.request(originalRequest);
    }

    // Not a 401, or no request object — just propagate
    if (status !== 401 || !originalRequest) {
      return Promise.reject(error);
    }

    // Don't infinite-loop: if the failing request IS the refresh endpoint,
    // or we've already retried, give up and log out.
    const isRefreshCall =
      originalRequest.url && originalRequest.url.includes('/auth/refresh');
    if (isRefreshCall || originalRequest._retried) {
      clearAuthAndRedirect();
      return Promise.reject(error);
    }

    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      clearAuthAndRedirect();
      return Promise.reject(error);
    }

    // Single-flight: if a refresh is already in progress, wait for it.
    if (!_refreshInFlight) {
      _refreshInFlight = axios
        .post(`${API_URL}/auth/refresh`, { refreshToken })
        .then((res) => {
          const data = res.data?.data || res.data;
          const newAccess = data?.accessToken || data?.token;
          const newRefresh = data?.refreshToken;
          if (!newAccess) throw new Error('No access token in refresh response');
          localStorage.setItem('token', newAccess);
          if (newRefresh) localStorage.setItem('refreshToken', newRefresh);
          // M4: resync the live socket with the refreshed token (see above).
          try { if (window.socketService?.isConnected) window.socketService.reconnect(); } catch (_) {}
          return newAccess;
        })
        .catch((err) => {
          clearAuthAndRedirect();
          throw err;
        })
        .finally(() => {
          // Clear the in-flight marker after the next microtask so any
          // requests that piggy-backed on this promise resolve first.
          setTimeout(() => { _refreshInFlight = null; }, 0);
        });
    }

    try {
      const newToken = await _refreshInFlight;
      originalRequest._retried = true;
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return api.request(originalRequest);
    } catch (refreshErr) {
      return Promise.reject(refreshErr);
    }
  }
);

// Auth API
// ============================================
// Firebase Phone OTP Authentication (Primary)
// ============================================
// Note: Password-based login/register have been removed.
// All authentication now uses Firebase Phone OTP.
// ============================================
export const authAPI = {
  getProfile: () => api.get('/auth/profile'),

  // Firebase Phone OTP Authentication
  verifyFirebaseToken: (idToken, role, options = {}) => api.post('/auth/firebase-verify', { idToken, role, ...options }),
  completeRegistration: (data) => api.post('/auth/complete-registration', data),
  checkPhone: (phone) => api.post('/auth/check-phone', { phone }),

  // Token management
  refreshToken: (refreshToken) => api.post('/auth/refresh', { refreshToken }),
  logout: (refreshToken) => api.post('/auth/logout', { refreshToken }),
  logoutAll: () => api.post('/auth/logout-all'),

  // FCM token management
  updateFcmToken: (fcmToken, platform) => api.post('/auth/fcm-token', { fcmToken, platform }),
  removeFcmToken: () => api.delete('/auth/fcm-token')
};

// User API
export const userAPI = {
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data) => api.put('/users/profile', data),
  addAddress: (data) => api.post('/users/addresses', data),
  getAddresses: () => api.get('/users/addresses'),
  updateAddress: (id, data) => api.put(`/users/addresses/${id}`, data),
  deleteAddress: (id) => api.delete(`/users/addresses/${id}`),
  updateDeliveryDetails: (data) => api.put('/users/delivery-details', data)
};

// Restaurant API
export const restaurantAPI = {
  getAll: (params) => api.get('/restaurants', { params }),
  getById: (id) => api.get(`/restaurants/${id}`),
  create: (data) => api.post('/restaurants', data),
  // Update order status (for accept orders - preparing, ready, etc.)
  updateOrderStatus: (orderId, data) => api.patch(`/orders/${orderId}/status`, data),
  // Cancel/Reject order (uses dedicated cancel endpoint with proper notification handling)
  cancelOrder: (orderId, reason) => api.patch(`/orders/${orderId}/cancel`, { cancellationReason: reason }),
  // Create with FormData (for photo upload in same request)
  createWithFormData: (formData) => api.post('/restaurants', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getMyRestaurant: () => api.get('/restaurants/owner/my-restaurant'),
  // Safe discount headroom (commission saved vs other apps) + own price markup for the dashboard nudge
  getPricingEdge: () => api.get('/restaurants/owner/pricing-edge'),
  // Upload an exact print preview the POS terminal rendered (admin live-preview flow)
  uploadPrintPreview: (data) => api.post('/restaurants/owner/print-preview', data),
  // Sales report CSV download (format: 'tally' | 'summary'); returns a blob
  downloadSalesReport: (params) => api.get('/restaurants/owner/sales-report', { params, responseType: 'blob' }),
  getMyOrders: () => api.get('/restaurants/owner/orders'),
  getOrders: (params) => api.get('/restaurants/owner/orders', { params }),
  getMyStats: () => api.get('/restaurants/owner/stats'),
  getEarningsSummary: (params) => api.get('/restaurants/owner/earnings', { params }),
  getEarnings: (params) => api.get('/restaurants/owner/earnings', { params }),
  getTransactionLedger: (params) => api.get('/restaurants/owner/transactions', { params }),
  // Instant payout - matches Flutter app endpoints
  getInstantPayoutInfo: () => api.get('/restaurants/owner/instant-payout/info'),
  requestInstantPayout: (amount) => api.post('/restaurants/owner/instant-payout/request', { amount }),
  cancelInstantPayout: (requestId) => api.post(`/restaurants/owner/instant-payout/${requestId}/cancel`),
  // Opening hours
  getOpeningHours: () => api.get('/restaurants/owner/my-restaurant'),
  updateOpeningHours: (data) => api.put('/restaurants/owner/opening-hours', data),
  update: (id, data) => api.put(`/restaurants/${id}`, data),
  updateStatus: (data) => api.patch('/restaurants/status', data),
  uploadImage: (formData) => api.post('/restaurants/upload-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteImage: (imageUrl) => api.delete('/restaurants/delete-image', { data: { imageUrl } }),

  // Onboarding endpoints
  getOnboardingStatus: () => api.get('/restaurants/onboarding/status'),
  uploadOnboardingImage: (formData) => api.post('/restaurants/onboarding/upload-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateOperationalDetails: (formData) => api.put('/restaurants/onboarding/operational-details', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteMenuImage: (imageUrl) => api.delete('/restaurants/onboarding/menu-image', { data: { imageUrl } }),
  submitDocuments: (formData) => api.put('/restaurants/onboarding/documents', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  submitContract: (data) => api.post('/restaurants/onboarding/contract', data),
  downloadContract: () => api.get('/restaurants/onboarding/download-contract', {
    responseType: 'blob'
  }),

  // Subscription plan endpoints
  selectSubscriptionPlan: (data) => api.post('/restaurants/subscription/select-plan', data),
  getSubscriptionPlanStatus: () => api.get('/restaurants/subscription/plan-status'),
  requestPlanChange: (data) => api.post('/restaurants/subscription/request-plan-change', data),

  // Featured restaurants
  getFeaturedRestaurants: (params) => api.get('/restaurants/featured', { params }),

  // P2: Payout Prediction & Export
  getPayoutPrediction: () => api.get('/restaurants/owner/payout-prediction'),
  exportEarnings: (params) => api.get('/restaurants/owner/export/earnings', {
    params,
    responseType: 'blob'
  }),

  // Bank Verification endpoints - matches Flutter app
  getBankVerificationStatus: () => api.get('/restaurants/owner/bank/status'),
  verifyBankAccount: (data) => api.post('/restaurants/owner/bank/verify', data),
  updateAndVerifyBank: (data) => api.put('/restaurants/owner/bank', data),

  // Notification Settings endpoints
  getNotificationSettings: () => api.get('/restaurants/owner/notification-settings'),
  updateNotificationSettings: (data) => api.put('/restaurants/owner/notification-settings', data),
  // Auto-accept orders setting
  getAutoAcceptSetting: () => api.get('/restaurants/owner/auto-accept'),
  updateAutoAcceptSetting: (data) => api.patch('/restaurants/owner/auto-accept', data)
};

// Menu API
export const menuAPI = {
  getMenuItems: (restaurantId, params) => api.get(`/menu/restaurant/${restaurantId}`, { params }),
  // Get all menu categories - matches Flutter app
  getCategories: () => api.get('/menu/categories'),
  addMenuItem: (formData) => api.post('/menu', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateMenuItem: (id, data) => api.put(`/menu/${id}`, data),
  toggleAvailability: (id, data) => api.patch(`/menu/${id}/availability`, data),
  toggleOutOfStock: (id, data) => api.patch(`/menu/${id}/out-of-stock`, data),
  deleteMenuItem: (id) => api.delete(`/menu/${id}`),
  // Standalone image upload (returns URL) - matches Flutter app
  uploadMenuImage: (formData) => api.post('/menu/upload-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Update specific item's image
  uploadImage: (id, formData) => api.post(`/menu/${id}/upload-image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Remove menu item image
  removeImage: (id) => api.delete(`/menu/${id}/image`)
};

// Order API
export const orderAPI = {
  create: (data) => api.post('/orders', data),
  getUserOrders: () => api.get('/orders/my-orders'),
  getById: (id) => api.get(`/orders/${id}`),
  updateStatus: (id, status) => api.patch(`/orders/${id}/status`, { status }),
  cancel: (id, reason) => api.patch(`/orders/${id}/cancel`, { cancellationReason: reason }),
  getDeliveryPartnerOrders: () => api.get('/delivery/my-deliveries'), // Delivery partner's orders
  updateLocation: (id, location) => api.patch(`/orders/${id}/location`, location), // Update delivery location
  getDeliveryFee: (restaurantId, lat, lng) => api.get(`/orders/delivery-fee/${restaurantId}`, { params: { lat, lng } }), // Calculate delivery fee
  // Cart preview - SINGLE SOURCE OF TRUTH for all financial calculations
  // Returns: subtotal, deliveryFee, platformFee, tax (GST), discount, total, grandTotal from backend
  getCartPreview: (data) => api.post('/orders/cart/preview', data, { timeout: 10000 })
};

// Delivery API
export const deliveryAPI = {
  // Onboarding
  getOnboardingStatus: () => api.get('/delivery/onboarding/status'),
  getOnboardingSettings: () => api.get('/delivery/onboarding-settings'), // Public route - no auth required
  updateWorkDetails: (data) => api.put('/delivery/onboarding/work-details', data),
  updateVehicleDetails: (data) => api.put('/delivery/onboarding/vehicle-details', data),
  submitIdDocuments: (formData) => api.put('/delivery/onboarding/id-documents', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  submitForApproval: (data) => api.post('/delivery/onboarding/submit', data),

  // Profile updates
  updatePersonalInfo: (data) => api.put('/delivery/profile/personal-info', data),
  updateBankDetails: (data) => api.put('/delivery/profile/bank-details', data),
  updateWorkAreas: (data) => api.put('/delivery/profile/work-areas', data),

  // Operations
  getAvailableOrders: () => api.get('/delivery/available-orders'),
  acceptOrder: (orderId) => api.post(`/delivery/accept/${orderId}`),
  getMyDeliveries: () => api.get('/delivery/my-deliveries'),
  updateAvailability: (isAvailable) => api.patch('/delivery/availability', { isAvailable }),
  updateStatus: (orderId, status) => api.patch(`/delivery/status/${orderId}`, { status }),
  updatePayment: (orderId) => api.patch(`/delivery/payment/${orderId}`),

  // COD Settlement Payment
  createCodSettlementPayment: (orderId) => api.post('/payments/create-cod-settlement-payment', { orderId }),
  verifyCodSettlementPayment: (orderId) => api.get(`/payments/verify-cod-settlement-payment/${orderId}`),

  getDashboard: () => api.get('/delivery/dashboard'),
  getEarningsSummary: () => api.get('/delivery/earnings/summary'),
  updateLocation: (location) => api.put('/delivery/location', location),
  getLocation: () => api.get('/delivery/location'),

  // P2: Payout Prediction & Export
  getPayoutPrediction: () => api.get('/delivery/payout-prediction'),
  exportEarnings: (params) => api.get('/delivery/export/earnings', {
    params,
    responseType: 'blob'
  })
};

// Admin API
export const adminAPI = {
  getAllOrders: (params) => api.get('/admin/orders', { params }),
  getChangeRequests: (params) => api.get('/admin/change-requests', { params }),
  approveChangeRequest: (id, note) => api.post(`/admin/change-requests/${id}/approve`, { note }),
  rejectChangeRequest: (id, note) => api.post(`/admin/change-requests/${id}/reject`, { note }),
  updateChangeApprovalSettings: (data) => api.patch('/admin/settings/restaurant-change-approval', data),
  // Daily social-media post generator
  getDailySocialToday: () => api.get('/admin/social-posts/today'),
  getDailySocialHistory: (params) => api.get('/admin/social-posts', { params }),
  generateDailySocialPost: (data) => api.post('/admin/social-posts/generate', data),
  updateDailySocialPost: (id, data) => api.patch(`/admin/social-posts/${id}`, data),
  deleteDailySocialPost: (id) => api.delete(`/admin/social-posts/${id}`),
  getDailySocialSettings: () => api.get('/admin/social-posts/settings'),
  updateDailySocialSettings: (data) => api.patch('/admin/social-posts/settings', data),
  uploadDailySocialReference: (formData) => api.post('/admin/social-posts/reference-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Brand Reel generator (vertical 1080x1920 MP4, built from the same brand profile).
  getSocialReels: (params) => api.get('/admin/social-reels', { params }),
  generateSocialReel: () => api.post('/admin/social-reels/generate', {}, { timeout: 180000 }),
  updateSocialReel: (id, data) => api.patch(`/admin/social-reels/${id}`, data),
  deleteSocialReel: (id) => api.delete(`/admin/social-reels/${id}`),
  updateSocialReelSettings: (data) => api.patch('/admin/social-reels/settings', data),
  // Full complete detail of a single order (every field, fully populated).
  getOrderDetail: (orderId) => api.get(`/admin/orders/${orderId}/detail`),
  // What this order's items are priced at on Swiggy/Zomato (menu-price comparison).
  getOrderCompetitorCost: (orderId) => api.get(`/admin/competitor-intel/order-cost/${orderId}`),
  getCartCompetitorCost: (userId) => api.get(`/admin/competitor-intel/cart-cost/${userId}`),
  getOrderStatusAudit: (params) => api.get('/admin/order-status-audit', { params }),
  getPersonalFinanceHub: (params) => api.get('/admin/personal-finance-hub', { params }),
  exportSales: (params) => api.get('/admin/sales-export', { params, responseType: 'blob' }),
  // Contacts export (Name + Phone) — type: 'users' | 'restaurants' | 'delivery'
  exportContacts: (type) => api.get('/admin/export/contacts', { params: { type }, responseType: 'blob' }),
  getPendingApprovals: () => api.get('/admin/approvals/pending'),
  updateApproval: (partnerId, isApproved) => api.patch(`/admin/approvals/${partnerId}`, { isApproved }),
  getAllReviews: () => api.get('/admin/reviews'),
  getRestaurantPerformance: () => api.get('/admin/performance/restaurants'),
  getDeliveryPerformance: () => api.get('/admin/performance/delivery'),
  getDashboard: () => api.get('/admin/dashboard'),
  // COD Autopay (UPI Autopay mandate) — on/off + cap settings, defaulters list, debit a rider
  codAutopayGetSettings: () => api.get('/admin/cod-autopay/settings'),
  codAutopayUpdateSettings: (data) => api.put('/admin/cod-autopay/settings', data),
  codAutopayDefaulters: (params) => api.get('/admin/cod-autopay/defaulters', { params }),
  codAutopayDebit: (partnerId, data) => api.post(`/admin/cod-autopay/debit/${partnerId}`, data),
  codSettlementPartners: () => api.get('/admin/cod-settlement/partners'),
  codSettlementOrders: (partnerId) => api.get(`/admin/cod-settlement/orders/${partnerId}`),
  codSettlementMarkPaid: (data) => api.post('/admin/cod-settlement/mark-paid', data),

  // Open a restaurant's own panel as the restaurant (admin support impersonation).
  impersonateRestaurant: (restaurantId) => api.post(`/admin/restaurants/${restaurantId}/impersonate`),

  // Competitor price intelligence (admin-only; Swiggy/Zomato vs our prices).
  ciGetSettings: () => api.get('/admin/competitor-intel/settings'),
  ciUpdateSettings: (data) => api.patch('/admin/competitor-intel/settings', data),
  ciGetRestaurants: () => api.get('/admin/competitor-intel/restaurants'),
  ciGetListings: () => api.get('/admin/competitor-intel/listings'),
  ciUpsertListing: (data) => api.post('/admin/competitor-intel/listings', data),
  ciDeleteListing: (id) => api.delete(`/admin/competitor-intel/listings/${id}`),
  ciScrapeOne: (id) => api.post(`/admin/competitor-intel/listings/${id}/scrape`),
  ciFindExternal: (data) => api.post('/admin/competitor-intel/find', data),
  ciTestProxy: () => api.get('/admin/competitor-intel/test-proxy'),
  ciScrapeAll: () => api.post('/admin/competitor-intel/scrape-all'),
  ciComparison: (restaurantId) => api.get(`/admin/competitor-intel/comparison/${restaurantId}`),
  ciExport: (restaurantId, params) => api.get(`/admin/competitor-intel/export/${restaurantId}`, { params, responseType: 'blob' }),
  ciApplyPhoto: (data) => api.post('/admin/competitor-intel/apply-photo', data),
  ciFullMenu: (restaurantId) => api.get(`/admin/competitor-intel/full-menu/${restaurantId}`),
  ciFullMenuExport: (restaurantId) => api.get(`/admin/competitor-intel/full-menu/${restaurantId}/export`, { responseType: 'blob' }),
  ciAiMatch: (restaurantId) => api.post(`/admin/competitor-intel/ai-match/${restaurantId}`),
  ciSetListingOffers: (id, offers) => api.put(`/admin/competitor-intel/listings/${id}/offers`, { offers }),
  ciMarkups: () => api.get('/admin/competitor-intel/markups'),
  ciWinLoss: () => api.get('/admin/competitor-intel/win-loss'),
  ciRunWinLoss: (days) => api.post('/admin/competitor-intel/win-loss/run', { days }),

  // Home "Featured" carousel — custom banner slides (separate from the paid featured_banner addon)
  getBannerSlides: () => api.get('/admin/banner-slides'),
  createBannerSlide: (formData) => api.post('/admin/banner-slides', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateBannerSlide: (id, formData) => api.put(`/admin/banner-slides/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteBannerSlide: (id) => api.delete(`/admin/banner-slides/${id}`),
  getCartBannerConfig: () => api.get('/admin/banner-slides/cart-config'),
  updateCartBannerConfig: (position) => api.put('/admin/banner-slides/cart-config', { position }),

  // Restaurant document review endpoints
  getAllRestaurants: (params) => api.get('/admin/restaurants', { params }),
  getPendingRestaurantReviews: () => api.get('/admin/restaurants/pending-reviews'),
  getRestaurantDetailsForReview: (restaurantId) => api.get(`/admin/restaurants/${restaurantId}/details`),
  verifyDocument: (restaurantId, documentType, verified) => api.patch(`/admin/restaurants/${restaurantId}/verify-document`, { documentType, verified }),
  reviewRestaurant: (restaurantId, approved, rejectionReason) => api.patch(`/admin/restaurants/${restaurantId}/review`, { approved, rejectionReason }),
  deleteRestaurant: (restaurantId) => api.delete(`/admin/restaurants/${restaurantId}`),

  // Subscription plan management endpoints
  getAllRestaurantSubscriptions: (params) => api.get('/admin/subscriptions/all', { params }),
  changeRestaurantPlan: (restaurantId, planType, reason) => api.patch(`/admin/subscriptions/${restaurantId}/change-plan`, { planType, reason }),
  getPlanChangeRequests: (params) => api.get('/admin/subscriptions/plan-change-requests', { params }),
  processPlanChangeRequest: (requestId, action, rejectionReason) => api.patch(`/admin/subscriptions/plan-change-request/${requestId}`, { action, rejectionReason }),
  getRestaurantSubscriptionDeductions: (restaurantId) => api.get(`/admin/subscriptions/${restaurantId}/deductions`),
  revertSubscriptionDeduction: (restaurantId, payoutId, reason) => api.post(`/admin/subscriptions/${restaurantId}/deductions/${payoutId}/revert`, { reason }),

  // Test simulation endpoints
  getAllUsers: () => api.get('/admin/users'),
  getAllDeliveryPartners: (params) => api.get('/admin/delivery-partners', { params }),
  // Delivery partner work analysis — per-day online/offline timeline + total online hours.
  // params: { date: 'YYYY-MM-DD', partnerId?: '<id>' }
  getDeliveryWorkAnalysis: (params) => api.get('/admin/delivery-work-analysis', { params }),
  createTestOrder: (data) => api.post('/admin/test-order', data),
  updateTestOrderStatus: (orderId, data) => api.patch(`/admin/test-order/${orderId}/status`, data),

  // Referral Team Management
  getReferralOverview: (params) => api.get('/admin/referral/overview', { params }),
  getPartnerReferralDetails: (partnerId) => api.get(`/admin/referral/partner/${partnerId}`),
  overrideTeamMember: (referrerId, memberId, action) => api.patch(`/admin/referral/override/${referrerId}/${memberId}`, { action }),
  getReferralEarningsReport: (params) => api.get('/admin/referral/earnings-report', { params }),

  // Support Team Management
  getSupportTeamMembers: () => api.get('/admin/support-team'),
  createSupportTeamMember: (data) => api.post('/admin/support-team', data),
  updateSupportTeamMember: (memberId, data) => api.patch(`/admin/support-team/${memberId}`, data),
  toggleSupportMemberStatus: (memberId) => api.patch(`/admin/support-team/${memberId}/status`),
  deleteSupportTeamMember: (memberId) => api.delete(`/admin/support-team/${memberId}`),
  // Restaurant Managers (multi-outlet accounts)
  getRestaurantManagers: () => api.get('/admin/restaurant-managers'),
  createRestaurantManager: (data) => api.post('/admin/restaurant-managers', data),
  updateRestaurantManager: (managerId, data) => api.patch(`/admin/restaurant-managers/${managerId}`, data),
  toggleRestaurantManagerStatus: (managerId) => api.patch(`/admin/restaurant-managers/${managerId}/status`),
  deleteRestaurantManager: (managerId) => api.delete(`/admin/restaurant-managers/${managerId}`),
  getReferralTeamSettings: () => api.get('/admin/settings/referral-team'),
  updateReferralTeamSettings: (data) => api.patch('/admin/settings/referral-team', data),

  // Restaurant Manager - Menu
  getRestaurantMenu: (restaurantId, params) => api.get(`/admin/restaurants/${restaurantId}/menu`, { params }),
  addRestaurantMenuItem: (restaurantId, formData) => api.post(`/admin/restaurants/${restaurantId}/menu`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Bulk add: scrape descriptions + classify (preview), then create reviewed items
  bulkScrapeMenuItems: (restaurantId, text, generateImages = false) => api.post(`/admin/restaurants/${restaurantId}/menu/bulk-scrape`, { text, generateImages }, { timeout: 180000 }),
  bulkCreateMenuItems: (restaurantId, items) => api.post(`/admin/restaurants/${restaurantId}/menu/bulk-create`, { items }, { timeout: 120000 }),
  updateRestaurantMenuItem: (restaurantId, menuId, data) => api.put(`/admin/restaurants/${restaurantId}/menu/${menuId}`, data),
  deleteRestaurantMenuItem: (restaurantId, menuId) => api.delete(`/admin/restaurants/${restaurantId}/menu/${menuId}`),
  toggleRestaurantMenuAvailability: (restaurantId, menuId) => api.patch(`/admin/restaurants/${restaurantId}/menu/${menuId}/availability`),
  toggleRestaurantMenuOutOfStock: (restaurantId, menuId) => api.patch(`/admin/restaurants/${restaurantId}/menu/${menuId}/out-of-stock`),
  setRestaurantMenuTopPick: (restaurantId, menuId, data) => api.patch(`/admin/restaurants/${restaurantId}/menu/${menuId}/top-pick`, data),
  setMenuItemDiscount: (restaurantId, menuId, data) => api.patch(`/admin/restaurants/${restaurantId}/menu/${menuId}/discount`, data),
  ciZomatoItemDiscounts: (restaurantId) => api.get(`/admin/competitor-intel/zomato-item-discounts/${restaurantId}`),
  setRestaurantCategoryOrder: (restaurantId, categoryOrder) => api.put(`/admin/restaurants/${restaurantId}/category-order`, { categoryOrder }),
  // Restaurant Manager - Coupons
  getRestaurantCoupons: (restaurantId) => api.get(`/admin/restaurants/${restaurantId}/coupons`),
  createRestaurantCoupon: (restaurantId, data) => api.post(`/admin/restaurants/${restaurantId}/coupons`, data),
  updateRestaurantCoupon: (restaurantId, couponId, data) => api.put(`/admin/restaurants/${restaurantId}/coupons/${couponId}`, data),
  deleteRestaurantCoupon: (restaurantId, couponId) => api.delete(`/admin/restaurants/${restaurantId}/coupons/${couponId}`),
  toggleRestaurantCouponStatus: (restaurantId, couponId) => api.patch(`/admin/restaurants/${restaurantId}/coupons/${couponId}/toggle`),
  // Restaurant Manager - Settings & Profile
  getRestaurantSettings: (restaurantId) => api.get(`/admin/restaurants/${restaurantId}/settings`),
  updateRestaurantSettings: (restaurantId, data) => api.patch(`/admin/restaurants/${restaurantId}/settings`, data),
  updateRestaurantProfile: (restaurantId, data) => api.patch(`/admin/restaurants/${restaurantId}/profile`, data),

  // Location management endpoints
  updateUserLocation: (userId, data) => api.patch(`/admin/users/${userId}/location`, data),
  updateRestaurantLocation: (restaurantId, data) => api.patch(`/admin/restaurants/${restaurantId}/location`, data),
  updateRestaurantHomePriority: (restaurantId, homePriority) => api.patch(`/admin/restaurants/${restaurantId}/home-priority`, { homePriority }),
  listPriorityZones: () => api.get('/admin/priority-zones'),
  priorityZoneRestaurants: () => api.get('/admin/priority-zones/restaurants'),
  createPriorityZone: (data) => api.post('/admin/priority-zones', data),
  updatePriorityZone: (id, data) => api.put(`/admin/priority-zones/${id}`, data),
  deletePriorityZone: (id) => api.delete(`/admin/priority-zones/${id}`),
  updateRestaurantPrintSettings: (restaurantId, data) => api.patch(`/admin/restaurants/${restaurantId}/print-settings`, data),
  getAttenderSettings: () => api.get('/admin/attender-settings'),
  updateAttenderSettings: (data) => api.patch('/admin/attender-settings', data),
  requestPrintPreview: (restaurantId, settings) => api.post(`/admin/restaurants/${restaurantId}/request-print-preview`, settings),
  getPrintPreview: (requestId) => api.get(`/admin/print-preview/${requestId}`),
  updateDeliveryPartnerLocation: (partnerId, data) => api.patch(`/admin/delivery-partners/${partnerId}/location`, data),

  // Live Delivery Map endpoints
  getDeliveryPartnersForMap: () => api.get('/admin/live-map/delivery-partners'),
  getDeliveryPartnerCurrentOrder: (partnerId) => api.get(`/admin/live-map/delivery-partners/${partnerId}/order`),

  // Delivery Assignment Tracker endpoints
  getDeliveryAssignmentTracker: (params) => api.get('/admin/assignment-tracker', { params }),
  getOrderAssignmentDetails: (orderId) => api.get(`/admin/assignment-tracker/${orderId}`),

  // Delivery Assignment Re-trigger endpoint
  retriggerDeliveryAssignment: (orderId) => api.post(`/admin/orders/${orderId}/retrigger-delivery`),
  approveOrder: (orderId) => api.post(`/admin/orders/${orderId}/approve`),
  rejectOrder: (orderId, reason) => api.post(`/admin/orders/${orderId}/reject`, { reason }),
  getPendingRefunds: () => api.get('/admin/refunds'),
  approveRefund: (orderId) => api.post(`/admin/refunds/${orderId}/approve`),
  denyRefund: (orderId, reason) => api.post(`/admin/refunds/${orderId}/deny`, { reason }),
  getBillingModels: (q) => api.get('/admin/billing-models', { params: q ? { q } : {} }),
  setRestaurantBillingModel: (id, payload) => api.patch(`/admin/restaurants/${id}/billing-model`, payload),
  setCompetitorMatchFunding: (id, funding) => api.patch(`/admin/restaurants/${id}/competitor-match-funding`, { funding }),
  setCompetitorMatchExempt: (id, exempt) => api.patch(`/admin/restaurants/${id}/competitor-match-exempt`, { exempt }),
  setCompetitorMatchPartial: (id, partial) => api.patch(`/admin/restaurants/${id}/competitor-match-partial`, { partial }),

  // Account blocking management endpoints
  updateUserBlockStatus: (userId, isBlocked, blockedReason) => api.patch(`/admin/users/${userId}/block-status`, { isBlocked, blockedReason }),
  // Force-logout / revoke all active sessions for a user (by userId OR phone) — no block.
  revokeUserSessions: (payload) => api.post('/admin/users/revoke-sessions', payload),
  updateRestaurantBlockStatus: (restaurantId, isBlocked, blockedReason) => api.patch(`/admin/restaurants/${restaurantId}/block-status`, { isBlocked, blockedReason }),
  toggleRestaurantVisibility: (restaurantId) => api.patch(`/admin/restaurants/${restaurantId}/visibility`),
  toggleRestaurantMultiCoupon: (restaurantId) => api.patch(`/admin/restaurants/${restaurantId}/multi-coupon`),
  updateDeliveryPartnerBlockStatus: (partnerId, isBlocked, blockedReason) => api.patch(`/admin/delivery-partners/${partnerId}/block-status`, { isBlocked, blockedReason }),

  // Admin override: force a delivery partner online/offline
  updateDeliveryPartnerAvailability: (partnerId, isAvailable) => api.patch(`/admin/delivery-partners/${partnerId}/availability`, { isAvailable }),

  // Per-partner online lock: block/allow a specific partner from going online
  setDeliveryPartnerOnlineAccess: (partnerId, allowed) => api.patch(`/admin/delivery-partners/${partnerId}/online-access`, { allowed }),

  // Per-partner: allow/restrict booking gigs across multiple work areas
  setDeliveryPartnerMultiAreaGigs: (partnerId, allowed) => api.patch(`/admin/delivery-partners/${partnerId}/multi-area-gigs`, { allowed }),

  // Master kill-switch: globally allow/block delivery partners going online
  getDeliveryPartnersOnline: () => api.get('/admin/settings/delivery-partners-online'),
  setDeliveryPartnersOnline: (enabled) => api.patch('/admin/settings/delivery-partners-online', { enabled }),

  // COD cash-in-hand limit — per-partner override + live status
  setDeliveryPartnerCodLimit: (partnerId, codLimit) => api.patch(`/admin/delivery-partners/${partnerId}/cod-limit`, { codLimit }),
  getDeliveryPartnerCodStatus: (partnerId) => api.get(`/admin/delivery-partners/${partnerId}/cod-status`),
  // COD cash-limit — global settings (master switch + default limit)
  getCodCashLimitSettings: () => api.get('/admin/settings/cod-cash-limit'),
  updateCodCashLimitSettings: (payload) => api.patch('/admin/settings/cod-cash-limit', payload),
  // Proactive subscription auto-renewal — global settings (master switch + grace days)
  getSubscriptionAutoRenewSettings: () => api.get('/admin/settings/subscription-auto-renew'),
  updateSubscriptionAutoRenewSettings: (payload) => api.patch('/admin/settings/subscription-auto-renew', payload),
  getRouteDistanceSettings: () => api.get('/admin/settings/route-distance'),
  updateRouteDistanceSettings: (payload) => api.patch('/admin/settings/route-distance', payload),
  getSubscriptionAutoRevertSettings: () => api.get('/admin/settings/subscription-auto-revert'),
  updateSubscriptionAutoRevertSettings: (payload) => api.patch('/admin/settings/subscription-auto-revert', payload),
  getSubscriptionAutoDeductSettings: () => api.get('/admin/settings/subscription-auto-deduct'),
  updateSubscriptionAutoDeductSettings: (payload) => api.patch('/admin/settings/subscription-auto-deduct', payload),

  // Delivery partner work area management
  updateDeliveryPartnerWorkAreas: (partnerId, workAreaIds) => api.patch(`/admin/delivery-partners/${partnerId}/work-areas`, { workAreaIds }),

  // Slot booking management endpoints
  getSlotDashboard: (date) => api.get('/admin/slots/dashboard', { params: { date } }),
  getSlotTemplates: (params) => api.get('/admin/slots/templates', { params }),
  updateSlotSurge: (templateId, slotId, { surgeMultiplier, capacity, baseIncentive }) => api.patch(`/admin/slots/templates/${templateId}/slots/${slotId}/surge`, { surgeMultiplier, capacity, baseIncentive }),
  bulkUpdateSlotSurge: (data) => api.post('/admin/slots/bulk-surge', data),
  getSlotBookings: (params) => api.get('/admin/slots/bookings', { params }),
  getSlotStats: (params) => api.get('/admin/slots/stats', { params }),
  generateSlotTemplates: (data) => api.post('/admin/slots/generate-templates', data),
  deleteSlotTemplate: (templateId) => api.delete(`/admin/slots/templates/${templateId}`),
  bulkDeleteSlotTemplates: (data) => api.post('/admin/slots/bulk-delete', data),
  regenerateEmptySlotTemplates: () => api.post('/admin/slots/regenerate-empty'),

  // Custom gig definitions (admin-created gigs of any time frame)
  getSlotDefinitions: () => api.get('/admin/slot-definitions'),
  createSlotDefinition: (data) => api.post('/admin/slot-definitions', data),
  updateSlotDefinition: (id, data) => api.put(`/admin/slot-definitions/${id}`, data),
  toggleSlotDefinition: (id) => api.patch(`/admin/slot-definitions/${id}/toggle`),
  deleteSlotDefinition: (id) => api.delete(`/admin/slot-definitions/${id}`),

  // City-wise slot penalty settings
  getCitySlotSettings: () => api.get('/admin/slots/city-settings'),
  updateCitySlotSettings: (cityId, data) => api.patch(`/admin/slots/city-settings/${cityId}`, data),
  deleteCitySlotSettings: (cityId) => api.delete(`/admin/slots/city-settings/${cityId}`),
  updateGlobalSlotSettings: (data) => api.patch('/admin/slots/global-settings', data),

  // Global slot limits
  getGlobalSlotLimits: () => api.get('/admin/slots/global-limits'),
  updateGlobalSlotLimits: (data) => api.patch('/admin/slots/global-limits', data),

  // Partner-specific slot limits
  getPartnersWithCustomLimits: () => api.get('/admin/slots/partner-limits'),
  getPartnerSlotLimits: (partnerId) => api.get(`/admin/slots/partner-limits/${partnerId}`),
  updatePartnerSlotLimits: (partnerId, data) => api.patch(`/admin/slots/partner-limits/${partnerId}`, data),
  resetPartnerSlotLimits: (partnerId) => api.delete(`/admin/slots/partner-limits/${partnerId}`),

  // Paid gig booking management
  getGigSettings: () => api.get('/admin/gig-settings'),
  updateGigSettings: (data) => api.patch('/admin/gig-settings', data),
  getGigExemptPartners: () => api.get('/admin/gig-exempt-partners'),
  toggleGigExemption: (partnerId, data) => api.patch(`/admin/gig-exemption/${partnerId}`, data),

  // Notification management endpoints
  getNotificationStats: () => api.get('/admin/notifications/stats'),
  // Export customers (name + phone) as Excel, scoped by push reachability
  exportNotificationCustomers: (filter) => api.get('/admin/notifications/export-customers', { params: { filter }, responseType: 'blob' }),
  getNotificationRecipients: (params) => api.get('/admin/notifications/recipients', { params }),
  sendBroadcastNotification: (data) => api.post('/admin/notifications/broadcast', data),
  sendNotificationToUser: (data) => api.post('/admin/notifications/send-to-user', data),
  uploadNotificationImage: (formData) => api.post('/admin/notifications/upload-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getWhatsAppCount: (params) => api.get('/admin/notifications/whatsapp-count', { params }),
  sendWhatsAppBroadcast: (data) => api.post('/admin/notifications/whatsapp-broadcast', data),
  pruneStaleTokens: (data) => api.post('/admin/notifications/prune-stale-tokens', data),

  // Coupon management endpoints
  getAllCoupons: (params) => api.get('/admin/coupons', { params }),
  getCouponAnalytics: () => api.get('/admin/coupons/analytics'),
  searchCouponCustomers: (q) => api.get('/admin/coupons/customer-search', { params: { q } }),
  resolveCouponSegment: (payload) => api.post('/admin/coupons/resolve-segment', payload),
  createUniversalCoupon: (data) => api.post('/admin/coupons', data),
  updateCoupon: (id, data) => api.put(`/admin/coupons/${id}`, data),
  deleteCoupon: (id) => api.delete(`/admin/coupons/${id}`),
  toggleCouponStatus: (id) => api.patch(`/admin/coupons/${id}/toggle`),

  // Subscription Addon Management endpoints
  // Addon Types
  getAllAddons: (params) => api.get('/admin/addons', { params }),
  getAddon: (addonId) => api.get(`/admin/addons/${addonId}`),
  createAddon: (data) => api.post('/admin/addons', data),
  updateAddon: (addonId, data) => api.put(`/admin/addons/${addonId}`, data),
  toggleAddonStatus: (addonId) => api.patch(`/admin/addons/${addonId}/toggle`),
  deleteAddon: (addonId) => api.delete(`/admin/addons/${addonId}`),
  seedDefaultAddons: () => api.post('/admin/addons/seed'),
  getAddonStats: () => api.get('/admin/addons/stats'),
  getCitiesForAddon: () => api.get('/admin/addons/cities'),
  getRestaurantsForAddon: (params) => api.get('/admin/addons/restaurants', { params }),

  // Addon Assignments
  getAllAddonAssignments: (params) => api.get('/admin/addons/assignments/all', { params }),
  getRestaurantAddonAssignments: (restaurantId) => api.get(`/admin/addons/assignments/restaurant/${restaurantId}`),
  assignAddonToRestaurant: (data) => api.post('/admin/addons/assign', data),
  updateAddonAssignment: (assignmentId, data) => api.patch(`/admin/addons/assignments/${assignmentId}`, data),
  cancelAddonAssignment: (assignmentId, reason) => api.post(`/admin/addons/assignments/${assignmentId}/cancel`, { reason }),
  renewAddonAssignment: (assignmentId, data) => api.post(`/admin/addons/assignments/${assignmentId}/renew`, data),

  // Addon Purchase Requests (from restaurants)
  getPendingAddonRequests: (params) => api.get('/admin/addons/requests', { params }),
  approveAddonRequest: (requestId) => api.post(`/admin/addons/requests/${requestId}/approve`),
  rejectAddonRequest: (requestId, data) => api.post(`/admin/addons/requests/${requestId}/reject`, data),

  // Surge Analytics endpoints
  getSurgeAnalytics: () => api.get('/admin/surge-analytics'),
  recalculateSurge: (workAreaId) => api.post(`/admin/surge-analytics/${workAreaId}/recalculate`),
  setSurgeMultiplier: (workAreaId, multiplier) => api.patch(`/admin/surge-analytics/${workAreaId}/multiplier`, { multiplier }),
  toggleSurgeAutoCalculation: (workAreaId, enabled) => api.patch(`/admin/surge-analytics/${workAreaId}/auto-calculation`, { enabled }),
  updateSurgePricingSettings: (data) => api.patch('/admin/settings/surge-pricing', data),

  // Delivery Milestone Rewards
  getMilestones: () => api.get('/admin/delivery-milestones'),
  createMilestone: (data) => api.post('/admin/delivery-milestones', data),
  updateMilestone: (id, data) => api.patch(`/admin/delivery-milestones/${id}`, data),
  deleteMilestone: (id) => api.delete(`/admin/delivery-milestones/${id}`),
  getMilestoneStats: (id) => api.get(`/admin/delivery-milestones/${id}/stats`),

  // Manual Adjustments (Credit/Debit)
  getManualAdjustments: (params) => api.get('/admin/manual-adjustments', { params }),
  createManualAdjustment: (data) => api.post('/admin/manual-adjustments', data),
  reverseManualAdjustment: (id, data) => api.post(`/admin/manual-adjustments/${id}/reverse`, data),

  // Infrastructure Monitoring
  getInfrastructureStatus: () => api.get('/admin/infrastructure'),

  // Server Metrics (Live CPU/Memory)
  getServerMetrics: () => api.get('/admin/server-metrics'),
  getServerMetricsLocal: () => api.get('/admin/server-metrics/local'),
  getServerMetricsHistory: (duration) => api.get('/admin/server-metrics/history', { params: { duration } }),

  // ==========================================
  // P2 ANALYTICS, PREDICTIONS & EXPORTS
  // ==========================================

  // Financial Analytics
  getFinancialDashboard: (params) => api.get('/admin/analytics/financial-dashboard', { params }),
  getMonthlyRevenue: (params) => api.get('/admin/analytics/monthly-revenue', { params }),
  getGrowthDiagnostics: () => api.get('/admin/analytics/growth-diagnostics'),
  getDeliveryDistanceReport: (params) => api.get('/admin/delivery-distance-report', { params }),
  backfillDeliveryDistance: (payload) => api.post('/admin/delivery-distance-report/backfill', payload),
  getDeliveryPartnerPrimaryLocations: () => api.get('/admin/delivery-partners/primary-locations'),
  updateDeliveryPartnerPrimaryLocation: (partnerId, payload) => api.patch(`/admin/delivery-partners/${partnerId}/primary-location`, payload),
  getEarningsTrend: (params) => api.get('/admin/analytics/earnings-trend', { params }),
  getPayoutTrend: (params) => api.get('/admin/analytics/payout-trend', { params }),
  getCommissionAnalytics: (params) => api.get('/admin/analytics/commissions', { params }),

  // Payout Predictions
  getPayoutForecast: (params) => api.get('/admin/predictions/payout-forecast', { params }),
  getRestaurantPrediction: (restaurantId) => api.get(`/admin/predictions/restaurant/${restaurantId}`),
  getDeliveryPartnerPrediction: (partnerId) => api.get(`/admin/predictions/delivery-partner/${partnerId}`),

  // Export Functions
  exportPlatformFinancials: (params) => api.get('/admin/export/platform-financials', {
    params,
    responseType: 'blob'
  }),
  exportPayouts: (params) => api.get('/admin/export/payouts', {
    params,
    responseType: 'blob'
  }),
  exportRestaurantEarnings: (restaurantId, params) => api.get(`/admin/export/restaurant/${restaurantId}/earnings`, {
    params,
    responseType: 'blob'
  }),
  exportDeliveryPartnerEarnings: (partnerId, params) => api.get(`/admin/export/delivery-partner/${partnerId}/earnings`, {
    params,
    responseType: 'blob'
  }),
  exportTdsReport: (params) => api.get('/admin/export/tds-report', {
    params,
    responseType: 'blob'
  }),
  exportGstReport: (params) => api.get('/admin/export/gst-report', {
    params,
    responseType: 'blob'
  }),

  // Rate Limit Management
  getRateLimitStatus: () => api.get('/admin/rate-limits'),
  unlockRateLimitKey: (key) => api.post('/admin/rate-limits/unlock', { key }),
  unlockRateLimitIP: (ip) => api.post('/admin/rate-limits/unlock-ip', { ip }),
  clearAllRateLimits: () => api.post('/admin/rate-limits/clear-all')
};

// Review API
export const reviewAPI = {
  create: (data) => api.post('/reviews', data),
  getRestaurantReviews: (restaurantId) => api.get(`/reviews/restaurant/${restaurantId}`),
  getByOrder: (orderId) => api.get(`/reviews/order/${orderId}`),
  // Restaurant owner reviews
  getMyReviews: (params) => api.get('/restaurants/owner/reviews', { params }),
  replyToReview: (reviewId, reply) => api.post(`/restaurants/owner/reviews/${reviewId}/reply`, { reply })
};

// Coupon API
export const couponAPI = {
  create: (data) => api.post('/coupons', data),
  getMyCoupons: () => api.get('/coupons/my-coupons'),
  getRestaurantCoupons: (restaurantId) => api.get(`/coupons/restaurant/${restaurantId}`),
  validate: (data) => api.post('/coupons/validate', data),
  update: (id, data) => api.put(`/coupons/${id}`, data),
  delete: (id) => api.delete(`/coupons/${id}`),
  toggleStatus: (id) => api.patch(`/coupons/${id}/toggle`)
};

// Search API
export const searchAPI = {
  searchRestaurants: (params) => api.get('/search', { params }),
  getSuggestions: (query) => api.get('/search/suggestions', { params: { q: query } })
};

// Payment API
export const paymentAPI = {
  createOrderPayment: (data) => api.post('/payments/create-order-payment', data),
  verifyPayment: (orderId) => api.get(`/payments/verify-payment/${orderId}`),
  settleCodOrder: (orderId) => api.patch(`/payments/settle-cod/${orderId}`),

  // Subscription payment endpoints
  createSubscriptionPayment: (data) => api.post('/payments/create-subscription-payment', data),
  verifySubscriptionPayment: (data) => api.post('/payments/verify-subscription-payment', data),

  // Remaining payment endpoints
  createRemainingPayment: (data) => api.post('/payments/create-remaining-payment', data),
  verifyRemainingPayment: (data) => api.post('/payments/verify-remaining-payment', data),

  // Plan change payment endpoints
  createPlanChangePayment: () => api.post('/payments/create-plan-change-payment'),
  verifyPlanChangePayment: (data) => api.post('/payments/verify-plan-change-payment', data),

  // Addon payment endpoints - matches Flutter app
  createAddonPayment: (requestId) => api.post('/payments/create-addon-payment', { requestId }),
  verifyAddonPayment: (requestId, cashfreeOrderId) => api.post('/payments/verify-addon-payment', { requestId, cashfreeOrderId })
};

// Settings API
export const settingsAPI = {
  getSettings: () => api.get('/admin/settings'),
  updateFreeDeliveryLimit: (freeDeliveryPartnerLimit) => api.patch('/admin/settings/free-delivery-limit', { freeDeliveryPartnerLimit }),
  updateSlotsUsed: (freeDeliverySlotsUsed) => api.patch('/admin/settings/slots-used', { freeDeliverySlotsUsed }),
  resetFreeSlotsUsed: () => api.patch('/admin/settings/reset-free-slots'),
  updateMaxRestaurantDistance: (maxRestaurantDistance) => api.patch('/admin/settings/max-restaurant-distance', { maxRestaurantDistance }),
  updateTrackingMapSettings: (payload) => api.patch('/admin/settings/tracking-map', payload),
  updateDeliveryAssignmentSettings: (settings) => api.patch('/admin/settings/delivery-assignment', settings),
  updateDeliveryPartnerSubsidy: (settings) => api.patch('/admin/settings/delivery-partner-subsidy', settings),
  // COD cash-in-hand limit — global master switch + default limit
  getCodCashLimitSettings: () => api.get('/admin/settings/cod-cash-limit'),
  updateCodCashLimitSettings: (data) => api.patch('/admin/settings/cod-cash-limit', data),
  // Telephony / IVR (Exotel) — opt-in on/off toggles (all default OFF)
  getTelephonySettings: () => api.get('/admin/settings/telephony'),
  updateTelephonySettings: (data) => api.patch('/admin/settings/telephony', data),
  testTelephonyCall: (data) => api.post('/admin/settings/telephony/test-call', data),
  // Customer Platform Fee Settings
  getCustomerPlatformFeeSettings: () => api.get('/admin/settings/customer-platform-fee'),
  updateCustomerPlatformFeeSettings: (data) => api.patch('/admin/settings/customer-platform-fee', data),
  // Tax Rate Settings (GST & TDS)
  getTaxRateSettings: () => api.get('/admin/settings/tax-rates'),
  updateTaxRateSettings: (taxRates) => api.patch('/admin/settings/tax-rates', taxRates),
  // Branding Image Upload
  uploadBrandingImage: (formData) => api.post('/admin/settings/upload-branding', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // App Version Settings (Force Update Feature)
  getAppVersionSettings: () => api.get('/admin/settings/app-versions'),
  updateAppVersionSettings: (appType, data) => api.put(`/admin/settings/app-versions/${appType}`, data),

  // Payment Gateway Settings (SDK vs WebView mode)
  getPaymentSettings: () => api.get('/admin/settings/payment-settings'),
  updatePaymentSettings: (data) => api.put('/admin/settings/payment-settings', data),

  // Notification Sound Settings
  getNotificationSoundSettings: () => api.get('/admin/settings/notification-sounds'),
  updateNotificationSoundSettings: (data) => api.put('/admin/settings/notification-sounds', data),
  uploadNotificationSound: (formData) => api.post('/admin/settings/upload-notification-sound', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteNotificationSound: (type) => api.delete(`/admin/settings/notification-sound/${type}`)
};

// City API
export const cityAPI = {
  getActiveCities: () => api.get('/cities/active'),
  getAllCities: (params) => api.get('/cities/all', { params }),
  getStates: () => api.get('/cities/states'),
  getIndianStates: () => api.get('/cities/indian-states'),
  getCitiesByState: (state) => api.get(`/cities/indian-cities/${encodeURIComponent(state)}`),
  addCity: (data) => api.post('/cities', data),
  updateCity: (id, data) => api.put(`/cities/${id}`, data),
  toggleCityStatus: (id) => api.patch(`/cities/${id}/toggle`),
  deleteCity: (id) => api.delete(`/cities/${id}`),
  validateCoordinates: (data) => api.post('/cities/validate-coordinates', data),
  // Subscription fee management
  getCitiesWithSubscriptionFees: () => api.get('/cities/subscription-fees'),
  updateCitySubscriptionFee: (id, monthlySubscriptionFee) => api.patch(`/cities/${id}/subscription-fee`, { monthlySubscriptionFee })
};

// Work Area API
export const workAreaAPI = {
  getAllWorkAreas: (params) => api.get('/work-areas/all', { params }),
  getWorkAreasByCity: (city) => api.get(`/work-areas/city/${encodeURIComponent(city)}`),
  createWorkArea: (data) => api.post('/work-areas', data),
  updateWorkArea: (id, data) => api.put(`/work-areas/${id}`, data),
  toggleWorkAreaStatus: (id) => api.patch(`/work-areas/${id}/toggle`),
  deleteWorkArea: (id) => api.delete(`/work-areas/${id}`),
  // Validate coordinates for a specific city - matches Flutter app
  validateCoordinates: (city, data) => api.post(`/work-areas/validate/${encodeURIComponent(city)}`, data)
};

// Support API
// Restaurant Manager (multi-outlet operator) API
export const restaurantManagerAPI = {
  login: (credentials) => api.post('/restaurant-manager/login', credentials),
  getMyRestaurants: () => api.get('/restaurant-manager/my-restaurants'),
  createSession: (restaurantId) => api.post(`/restaurant-manager/session/${restaurantId}`),
};

export const supportAPI = {
  // User routes
  createTicket: (data) => api.post('/support/ticket', data),
  getMyTickets: (params) => api.get('/support/my-tickets', { params }),
  getTicketById: (ticketId) => api.get(`/support/ticket/${ticketId}`),
  addResponse: (ticketId, data) => api.post(`/support/ticket/${ticketId}/response`, data),
  closeTicket: (ticketId) => api.put(`/support/ticket/${ticketId}/close`),

  // Admin routes
  getAllTickets: (params) => api.get('/support/all-tickets', { params }),
  getStatistics: (params) => api.get('/support/statistics', { params }),
  updateTicketStatus: (ticketId, data) => api.put(`/support/ticket/${ticketId}/status`, data),

  // Restaurant Manager (support-scoped)
  getMyRestaurants: () => api.get('/support/my-restaurants'),
  getRestaurantMenu: (restaurantId, params) => api.get(`/support/restaurants/${restaurantId}/menu`, { params }),
  addMenuItem: (restaurantId, data) => api.post(`/support/restaurants/${restaurantId}/menu`, data),
  updateMenuItem: (restaurantId, menuId, data) => api.put(`/support/restaurants/${restaurantId}/menu/${menuId}`, data),
  deleteMenuItem: (restaurantId, menuId) => api.delete(`/support/restaurants/${restaurantId}/menu/${menuId}`),
  toggleMenuAvailability: (restaurantId, menuId) => api.patch(`/support/restaurants/${restaurantId}/menu/${menuId}/availability`),
  toggleOutOfStock: (restaurantId, menuId) => api.patch(`/support/restaurants/${restaurantId}/menu/${menuId}/out-of-stock`),
  getRestaurantCoupons: (restaurantId) => api.get(`/support/restaurants/${restaurantId}/coupons`),
  createCoupon: (restaurantId, data) => api.post(`/support/restaurants/${restaurantId}/coupons`, data),
  updateCoupon: (restaurantId, couponId, data) => api.put(`/support/restaurants/${restaurantId}/coupons/${couponId}`, data),
  deleteCoupon: (restaurantId, couponId) => api.delete(`/support/restaurants/${restaurantId}/coupons/${couponId}`),
  toggleCoupon: (restaurantId, couponId) => api.patch(`/support/restaurants/${restaurantId}/coupons/${couponId}/toggle`),
  getRestaurantSettings: (restaurantId) => api.get(`/support/restaurants/${restaurantId}/settings`),
  updateRestaurantSettings: (restaurantId, data) => api.patch(`/support/restaurants/${restaurantId}/settings`, data),
  updateRestaurantProfile: (restaurantId, data) => api.patch(`/support/restaurants/${restaurantId}/profile`, data),

  // Orders tab (support — full access, mirrors adminAPI). Names match adminAPI so
  // the shared Admin/Orders + LiveDeliveryMap pages can pick the namespace by role.
  getAllOrders: (params) => api.get('/support/orders', { params }),
  getOrderDetail: (orderId) => api.get(`/support/orders/${orderId}/detail`),
  updateTestOrderStatus: (orderId, data) => api.patch(`/support/orders/${orderId}/status`, data),
  approveOrder: (orderId) => api.post(`/support/orders/${orderId}/approve`),
  rejectOrder: (orderId, reason) => api.post(`/support/orders/${orderId}/reject`, { reason }),
  retriggerDeliveryAssignment: (orderId) => api.post(`/support/orders/${orderId}/retrigger-delivery`),
  // Live map tab (support)
  getDeliveryPartnersForMap: () => api.get('/support/live-map/delivery-partners'),
  getDeliveryPartnerCurrentOrder: (partnerId) => api.get(`/support/live-map/delivery-partners/${partnerId}/order`)
};

// Payout API
export const payoutAPI = {
  // Statistics and balance
  getStats: () => api.get('/payouts/stats'),
  getBalance: () => api.get('/payouts/balance'),
  getMode: () => api.get('/payouts/mode'),

  // Get all payouts
  getAllPayouts: (params) => api.get('/payouts/all', { params }),

  // Get recipients for payout (optional { startDate, endDate } to scope to a window)
  getRestaurantsForPayout: (params) => api.get('/payouts/restaurants', { params }),
  getDeliveryPartnersForPayout: () => api.get('/payouts/delivery-partners'),

  // Bank account validation
  validateBankAccount: (data) => api.post('/payouts/validate-bank', data),

  // Beneficiary management
  addBeneficiary: (data) => api.post('/payouts/beneficiary', data),

  // Update bank details
  updateBankDetails: (recipientType, recipientId, data) =>
    api.put(`/payouts/bank-details/${recipientType}/${recipientId}`, data),

  // Bank verification
  verifyBank: (entityType, entityId) =>
    api.post('/payouts/bank/verify', { entityType, entityId }),
  getBankStatus: (entityType, entityId) =>
    api.get(`/payouts/bank/status/${entityType}/${entityId}`),

  // Payout operations
  createPayout: (data) => api.post('/payouts/create', data),
  createRestaurantPayout: (data) => api.post('/payouts/restaurant/create-with-subscription', data),
  processPayout: (payoutId, data = {}) => api.post(`/payouts/${payoutId}/process`, data),
  disbursePayout: (payoutId, data = {}) => api.post(`/payouts/${payoutId}/disburse`, data),
  getPayoutStatus: (payoutId) => api.get(`/payouts/${payoutId}/status`),
  cancelPayout: (payoutId, reason) => api.post(`/payouts/${payoutId}/cancel`, { reason }),
  retryPayout: (payoutId) => api.post(`/payouts/${payoutId}/retry`),

  // Payout settings (admin)
  getPayoutSettings: () => api.get('/admin/settings/payout'),
  updatePayoutSettings: (data) => api.patch('/admin/settings/payout', data),

  // Instant Payout Requests (admin)
  getInstantPayoutRequests: (params) => api.get('/admin/instant-payout-requests', { params }),
  approveInstantPayoutRequest: (requestId) => api.post(`/admin/instant-payout-requests/${requestId}/approve`),
  rejectInstantPayoutRequest: (requestId, reason) => api.post(`/admin/instant-payout-requests/${requestId}/reject`, { reason }),

  // Order Financials (admin)
  getOrderFinancials: (params) => api.get('/payouts/order-financials', { params }),

  // ==========================================
  // P2 OPERATIONS - Failed Payouts & Retry
  // ==========================================
  getFailedPayouts: (params) => api.get('/admin/payouts/failed', { params }),
  bulkRetryPayouts: (payoutIds) => api.post('/admin/payouts/bulk-retry', { payoutIds }),
  getRetryStats: () => api.get('/admin/payouts/retry-stats'),
  getCircuitBreakerStatus: () => api.get('/admin/payouts/circuit-breaker/status'),
  resetCircuitBreaker: () => api.post('/admin/payouts/circuit-breaker/reset'),

  // ==========================================
  // P2 OPERATIONS - Webhook Queue Management
  // ==========================================
  getWebhookQueueStats: () => api.get('/admin/webhook-queue/stats'),
  getPendingWebhooks: (params) => api.get('/admin/webhook-queue/pending', { params }),
  getDeadLetterQueue: (params) => api.get('/admin/webhook-queue/dead-letter', { params }),
  retryWebhook: (queueId) => api.post(`/admin/webhook-queue/${queueId}/retry`),
  processWebhookQueue: () => api.post('/admin/webhook-queue/process'),
  startWebhookProcessor: () => api.post('/admin/webhook-queue/processor/start'),
  stopWebhookProcessor: () => api.post('/admin/webhook-queue/processor/stop'),
  cleanupWebhookQueue: (data) => api.post('/admin/webhook-queue/cleanup', data),

  // ==========================================
  // P2 OPERATIONS - Reconciliation
  // ==========================================
  getReconciliationStatus: () => api.get('/admin/reconciliation/status'),
  runReconciliation: (data) => api.post('/admin/reconciliation/run', data),
  getReconciliationReports: (params) => api.get('/admin/reconciliation/reports', { params })
};

// Addon API (Restaurant Owner) - matches Flutter app endpoints
export const addonAPI = {
  // Available addons
  getAvailableAddons: () => api.get('/restaurants/subscription/addons/available'),
  // My active addons
  getMyAddons: () => api.get('/restaurants/subscription/addons/my-addons'),
  // My addon requests
  getMyAddonRequests: () => api.get('/restaurants/subscription/addons/my-requests'),
  // Create addon request
  createAddonRequest: (data) => api.post('/restaurants/subscription/addons/request', data),
  // Cancel addon request
  cancelAddonRequest: (requestId) => api.post(`/restaurants/subscription/addons/request/${requestId}/cancel`)
};

// Delivery Fee Config API (Admin)
export const deliveryFeeConfigAPI = {
  // Get all configs
  getAllConfigs: (params) => api.get('/delivery-fee-config', { params }),

  // Get default config
  getDefaultConfig: () => api.get('/delivery-fee-config/default'),

  // Get config by ID
  getConfigById: (id) => api.get(`/delivery-fee-config/${id}`),

  // Get cities with their configs
  getCitiesWithConfigs: () => api.get('/delivery-fee-config/cities'),

  // Get work areas with their configs
  getWorkAreasWithConfigs: (city) => api.get('/delivery-fee-config/work-areas', { params: { city } }),

  // Create new config
  createConfig: (data) => api.post('/delivery-fee-config', data),

  // Update config
  updateConfig: (id, data) => api.put(`/delivery-fee-config/${id}`, data),

  // Delete config
  deleteConfig: (id) => api.delete(`/delivery-fee-config/${id}`),

  // Preview fee calculation
  previewFee: (data) => api.post('/delivery-fee-config/preview', data)
};

// Invoice API (Admin)
export const invoiceAPI = {
  // Admin endpoints
  getAll: (params) => api.get('/invoices/admin', { params }),
  getById: (id) => api.get(`/invoices/admin/${id}`),
  getStats: (params) => api.get('/invoices/admin/stats', { params }),
  regeneratePDF: (id) => api.post(`/invoices/admin/${id}/regenerate-pdf`),
  emailInvoice: (id, email) => api.post(`/invoices/admin/${id}/email`, { email }),
  generateMonthlyTDS: (month, year) => api.post('/invoices/admin/tds/generate-monthly', { month, year }),
  generateCreditNote: (data) => api.post('/invoices/admin/credit-note', data),
  generateDebitNote: (data) => api.post('/invoices/admin/debit-note', data),

  // Platform settings
  getPlatformSettings: () => api.get('/invoices/admin/settings/platform'),
  updatePlatformSettings: (data) => api.put('/invoices/admin/settings/platform', data)
};

// Invoice API (Restaurant Owner) - matches Flutter app endpoints
export const restaurantInvoiceAPI = {
  getAll: (params) => api.get('/restaurants/owner/invoices', { params }),
  getById: (id) => api.get(`/restaurants/owner/invoices/${id}`),
  download: (id) => api.get(`/restaurants/owner/invoices/${id}/download`),
  getTDSStatements: (params) => api.get('/restaurants/owner/tds-statements', { params }),
  getSettlementStatements: (params) => api.get('/restaurants/owner/settlement-statements', { params })
};

// Invoice API (Customer/User)
export const userInvoiceAPI = {
  getAll: (params) => api.get('/invoices/user', { params }),
  getOrderInvoices: (orderId) => api.get(`/invoices/user/order/${orderId}`),
  download: (id) => api.get(`/invoices/user/${id}/download`)
};

// Call API (Voice Calls with Agora) - matches Flutter app endpoints
export const callAPI = {
  // Get call configuration (Agora app ID)
  getConfig: () => api.get('/calls/config'),
  // Initiate a call
  initiateCall: (data) => api.post('/calls/initiate', data),
  // Accept incoming call
  acceptCall: (callId) => api.post(`/calls/accept/${callId}`),
  // Reject incoming call
  rejectCall: (callId) => api.post(`/calls/reject/${callId}`),
  // End ongoing call
  endCall: (callId) => api.post(`/calls/end/${callId}`)
};

// Finance Audit API (Admin)
export const financeAuditAPI = {
  // Dashboard
  getDashboard: (params) => api.get('/finance-audit/dashboard', { params }),

  // Transactions
  getTransactions: (params) => api.get('/finance-audit/transactions', { params }),
  getTransactionSummary: (params) => api.get('/finance-audit/transactions/summary', { params }),
  getTransaction: (auditId) => api.get(`/finance-audit/transactions/${auditId}`),

  // Reconciliation
  getReconciliations: (params) => api.get('/finance-audit/reconciliations', { params }),
  getReconciliation: (id) => api.get(`/finance-audit/reconciliations/${id}`),
  generateReconciliation: (data) => api.post('/finance-audit/reconciliations/generate', data),
  approveReconciliation: (id, data) => api.post(`/finance-audit/reconciliations/${id}/approve`, data),

  // Order & Payout Audit
  getOrderAudit: (orderId) => api.get(`/finance-audit/orders/${orderId}/audit`),
  getPayoutAudit: (payoutId) => api.get(`/finance-audit/payouts/${payoutId}/audit`),

  // Reports
  getRevenueReport: (params) => api.get('/finance-audit/reports/revenue', { params }),
  getCommissionReport: (params) => api.get('/finance-audit/reports/commissions', { params }),
  getTdsReport: (params) => api.get('/finance-audit/reports/tds', { params }),
  getGstReport: (params) => api.get('/finance-audit/reports/gst', { params }),
  getSubscriptionReport: (params) => api.get('/finance-audit/reports/subscriptions', { params }),

  // Discrepancies
  getDiscrepancies: (params) => api.get('/finance-audit/discrepancies', { params }),
  resolveDiscrepancy: (reconciliationId, index, data) =>
    api.post(`/finance-audit/discrepancies/${reconciliationId}/${index}/resolve`, data),

  // Adjustments
  createAdjustment: (data) => api.post('/finance-audit/adjustments', data),

  // Export
  exportAuditData: (params) => api.get('/finance-audit/export', { params })
};

// CA Audit Reports API (CA-grade financial audit reports)
export const caAuditAPI = {
  // Dashboard
  getDashboard: (params) => api.get('/ca-audit/dashboard', { params }),

  // GSTR-1 Reports
  getGSTR1: (params) => api.get('/ca-audit/gstr1', { params }),
  getGSTR1B2B: (params) => api.get('/ca-audit/gstr1/b2b', { params }),
  getGSTR1B2C: (params) => api.get('/ca-audit/gstr1/b2c', { params }),
  getGSTR1StateWise: (params) => api.get('/ca-audit/gstr1/state-wise', { params }),
  exportGSTR1: (params) => api.get('/ca-audit/gstr1/export', { params, responseType: 'blob' }),

  // GSTR-3B Reports
  getGSTR3B: (params) => api.get('/ca-audit/gstr3b', { params }),
  exportGSTR3B: (params) => api.get('/ca-audit/gstr3b/export', { params, responseType: 'blob' }),

  // TDS Reports (Section 194-O)
  getTDSRegister: (params) => api.get('/ca-audit/tds/register', { params }),
  getTDSDeductees: (params) => api.get('/ca-audit/tds/deductees', { params }),
  getTDSQuarterly: (params) => api.get('/ca-audit/tds/quarterly', { params }),
  getTDSCertificates: (params) => api.get('/ca-audit/tds/certificates', { params }),
  exportTDS: (params) => api.get('/ca-audit/tds/export', { params, responseType: 'blob' }),

  // HSN/SAC Summary
  getHSNSACSummary: (params) => api.get('/ca-audit/hsn-sac/summary', { params }),
  getHSNSACRateWise: (params) => api.get('/ca-audit/hsn-sac/rate-wise', { params }),
  exportHSNSAC: (params) => api.get('/ca-audit/hsn-sac/export', { params, responseType: 'blob' }),

  // Invoice Registers
  getSalesRegister: (params) => api.get('/ca-audit/registers/sales', { params }),
  getSettlementRegister: (params) => api.get('/ca-audit/registers/settlements', { params }),
  getCreditNoteRegister: (params) => api.get('/ca-audit/registers/credit-notes', { params }),
  getDebitNoteRegister: (params) => api.get('/ca-audit/registers/debit-notes', { params }),
  getDPEarningsRegister: (params) => api.get('/ca-audit/registers/delivery-earnings', { params }),
  exportRegister: (params) => api.get('/ca-audit/registers/export', { params, responseType: 'blob' }),

  // Settlement Reconciliation
  getSettlementReconciliation: (params) => api.get('/ca-audit/settlement/reconciliation', { params }),
  getSettlementDiscrepancies: (params) => api.get('/ca-audit/settlement/discrepancies', { params }),
  getBankTracking: (params) => api.get('/ca-audit/settlement/bank-tracking', { params }),
  exportSettlement: (params) => api.get('/ca-audit/settlement/export', { params, responseType: 'blob' }),

  // Round-off Report
  getRoundOffReport: (params) => api.get('/ca-audit/round-off/report', { params }),
  getRoundOffSummary: (params) => api.get('/ca-audit/round-off/summary', { params }),
  exportRoundOff: (params) => api.get('/ca-audit/round-off/export', { params, responseType: 'blob' }),

  // Revenue Recognition
  getRevenueRecognition: (params) => api.get('/ca-audit/revenue/recognition', { params }),
  getRevenueCutoff: (params) => api.get('/ca-audit/revenue/cutoff', { params }),
  getDeferredRevenue: (params) => api.get('/ca-audit/revenue/deferred', { params }),
  exportRevenue: (params) => api.get('/ca-audit/revenue/export', { params, responseType: 'blob' }),

  // Trial Balance & P&L
  getTrialBalance: (params) => api.get('/ca-audit/trial-balance', { params }),
  getProfitLoss: (params) => api.get('/ca-audit/profit-loss', { params }),
  exportFinancialStatements: (params) => api.get('/ca-audit/financial-statements/export', { params, responseType: 'blob' }),

  // Master Excel Export
  exportExcelWorkbook: (params) => api.get('/ca-audit/export/excel-workbook', { params, responseType: 'blob' })
};

// Public Settings API (no authentication required)
export const publicSettingsAPI = {
  getNotificationSoundSettings: () => api.get('/settings/notification-sounds')
};

// Finance Hub — platform expense management + net-profit view.
// Revenue for the P&L is reused from the existing adminAPI.getFinancialDashboard.
export const expenseAPI = {
  getCategories: () => api.get('/admin/expenses/categories'),
  getExpenses: (params) => api.get('/admin/expenses', { params }),
  getSummary: (params) => api.get('/admin/expenses/summary', { params }),
  getById: (id) => api.get(`/admin/expenses/${id}`),
  create: (payload) => api.post('/admin/expenses', payload),
  update: (id, payload) => api.put(`/admin/expenses/${id}`, payload),
  void: (id, reason) => api.post(`/admin/expenses/${id}/void`, { reason }),
  export: (params) => api.get('/admin/expenses/export', { params, responseType: 'blob' }),
  getRestaurants: (params) => api.get('/admin/finance-hub/restaurants', { params }),
  getDeliveryPartners: (params) => api.get('/admin/finance-hub/delivery-partners', { params })
};

// Abandoned-cart recovery engine (admin).
export const cartRecoveryAPI = {
  getSettings: () => api.get('/admin/cart-recovery/settings'),
  updateSettings: (payload) => api.patch('/admin/cart-recovery/settings', payload),
  getStats: (params) => api.get('/admin/cart-recovery/stats', { params })
};

// Proactive re-engagement engine (admin).
export const reEngagementAPI = {
  getSettings: () => api.get('/admin/re-engagement/settings'),
  updateSettings: (payload) => api.patch('/admin/re-engagement/settings', payload),
  getStats: (params) => api.get('/admin/re-engagement/stats', { params })
};

export const aiMessagingAPI = {
  getSettings: () => api.get('/admin/ai-messaging/settings'),
  updateSettings: (payload) => api.put('/admin/ai-messaging/settings', payload),
  getContext: () => api.get('/admin/ai-messaging/context'),
  preview: (payload) => api.post('/admin/ai-messaging/preview', payload),
  getVariants: () => api.get('/admin/ai-messaging/variants'),
};

// Mithai Special — admin-curated sweets collection for the user-app home.
export const mithaiSpecialAPI = {
  getSettings: () => api.get('/admin/mithai-special'),
  updateSettings: (payload) => api.put('/admin/mithai-special', payload),
};

export const localFamousKartsAPI = {
  getSettings: () => api.get('/admin/local-famous-karts'),
  updateSettings: (payload) => api.put('/admin/local-famous-karts', payload),
};

export const deliveryServiceAPI = {
  getConfig: () => api.get('/admin/delivery-service-config'),
  updateConfig: (payload) => api.put('/admin/delivery-service-config', payload),
};

// FoodWinc Wallet settings (enable, recharge limits, cashback).
export const walletAdminAPI = {
  getSettings: () => api.get('/admin/wallet-settings'),
  updateSettings: (payload) => api.put('/admin/wallet-settings', payload),
  getAnalytics: (params) => api.get('/admin/wallet-analytics', { params }),
  lookupUser: (phone) => api.get('/admin/wallet-user', { params: { phone } }),
  adjust: (payload) => api.post('/admin/wallet-adjust', payload),
};

// OTP provider (Firebase vs MSG91) — global default + per-app (role) overrides.
export const otpAdminAPI = {
  getSettings: () => api.get('/admin/settings/otp'),
  updateSettings: (payload) => api.patch('/admin/settings/otp', payload),
};

// Daily "order food" nudge broadcast (admin).
export const dailyNudgeAPI = {
  getSettings: () => api.get('/admin/daily-nudge/settings'),
  updateSettings: (payload) => api.put('/admin/daily-nudge/settings', payload),
  preview: (payload) => api.post('/admin/daily-nudge/preview', payload),
  testSend: (payload) => api.post('/admin/daily-nudge/test-send', payload),
  getStats: () => api.get('/admin/daily-nudge/stats'),
};

// User behavior analytics + RFM/discount segmentation (admin).
export const behaviorAPI = {
  getTracking: () => api.get('/admin/behavior/tracking'),
  setTracking: (enabled) => api.patch('/admin/behavior/tracking', { enabled }),
  getOverview: (params) => api.get('/admin/behavior/overview', { params }),
  getEventUsers: (params) => api.get('/admin/behavior/event-users', { params }),
  getNotificationOpens: (days) => api.get('/admin/behavior/notification-opens', { params: { days } }),
  getSegments: () => api.get('/admin/behavior/segments'),
  getUsers: (params) => api.get('/admin/behavior/users', { params }),
  recompute: () => api.post('/admin/behavior/recompute')
};

export default api;
